import React, { useCallback, useContext, useState } from 'react';
import { Drawer, Link } from '@printdeal/ui-library';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { addDataLayerEntry, addEventEntry } from '@printdeal/data-layer-manager';
import type { NavigationContainer } from '../../../types';
import NavigationExperiment from '../../../NavigationExperiment';
import MobileFeaturedNavigation from './MobileFeaturedNavigation';
import MobileAllCategoriesNavigation from './MobileAllCategoriesNavigation';
import MobileSupplementaryNavigation from './MobileSupplementaryNavigation';
import LanguageSwitch from '../../language-switch';
import { Paths } from '../../../../../types/gatsby';
import { dispatchNavigationHotjarEvent } from '../helpers';
import { HeaderContext } from '../../context';

type MobileNavigationProps = {
  localizedPaths?: Paths;
  allCategoriesNavigation: NavigationContainer['navigation'];
  featuredNavigation: NavigationContainer['navigation'];
  supplementaryNavigation: NavigationContainer['navigation'];
  className?: string;
};

const MobileNavigation = ({
  localizedPaths,
  allCategoriesNavigation,
  featuredNavigation,
  supplementaryNavigation,
  className,
}: MobileNavigationProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { isSticky } = useContext(HeaderContext);

  const handleMobileNavigationOpenClick = useCallback(() => {
    setIsOpen(true);

    dispatchNavigationHotjarEvent();
    const eventCategory = !isSticky ? 'navigation_mobile_show_sticky' : 'navigation_mobile_show';
    const eventAction = 'click';
    addDataLayerEntry(
      eventCategory,
      {
        eventAction,
      },
    );
    addEventEntry({
      eventCategory,
      eventAction,
    });
    addDataLayerEntry('navigation_mobile_show');
  }, [isSticky]);

  return (
    <div className={clsx('pd-flex lg:pd-hidden', className)}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        as="button"
        type="button"
        variant="secondary"
        iconBefore="bars"
        onClick={handleMobileNavigationOpenClick}
      >
        {intl.formatMessage({ id: 'global.menu.toggle', defaultMessage: 'Menu' })}
      </Link>
      <Drawer
        isOpen={isOpen}
        title={intl.formatMessage({ id: 'global.menu.toggle', defaultMessage: 'Menu' })}
        dismissLabel={intl.formatMessage({
          id: 'account.general.modal.close',
          defaultMessage: 'Close',
        })}
        onClose={setIsOpen}
      >
        <div className="pd-flex pd-flex-col pd-gap-5 pd-pb-12">
          <NavigationExperiment as={MobileFeaturedNavigation} navigation={featuredNavigation} />
          <hr />
          <NavigationExperiment as={MobileAllCategoriesNavigation} navigation={allCategoriesNavigation} />
          <hr />
          <NavigationExperiment as={MobileSupplementaryNavigation} navigation={supplementaryNavigation} />
          {localizedPaths && (
            <>
              <hr />
              <LanguageSwitch localizedPaths={localizedPaths} />
            </>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default MobileNavigation;
